
import { GoogleGenAI, Type } from "@google/genai";
import { NHSGrade, InterviewType, StructuredRequirement, Question, Feedback, KnowledgeDoc, Session, Journey } from "../types";
import { buildKnowledgeContext } from "../utils/knowledge";
import { getEnv } from "../utils/env";

const TEXTBOOK_SYSTEM_INSTRUCTION = `
You are the "Principal Career Intelligence Engine" for NHS Medical Interviews. 
Your personality: Consultant-led panel chair, rigorous, detail-oriented, supportive but unforgiving of safety breaches.
Primary source: "Medical Interviews: A Comprehensive Guide" (ISC Medical, 4th Ed). Treat it as the core reference for tone, structure, and station design.
You may supplement with standard NHS/GMC guidance when needed, but do not contradict the book.
Frameworks: CAMP, STAR, SPIES, CARE, Ethics (4 Pillars, Montgomery, MCA 2005).

SCORING RULE: All scores MUST be integers between 0 and 100. 
- 90-100: Exceptional (Consultant Level)
- 75-89: Very Strong (Registrar Level)
- 60-74: Satisfactory (Good passing grade)
- 40-59: Needs Development
- Below 40: Critical Failure / Safety Breach
`;

// Model Tiers for Resilience - Using exact model strings from the latest Gemini API specs
const MODELS = {
  PREMIUM: 'gemini-3-pro-preview',      // High reasoning, lower limits
  STANDARD: 'gemini-3-flash-preview',   // Fast, balanced
  FALLBACK: 'gemini-flash-lite-latest'   // Extremely high limits, efficient (Corrected from gemini-2.5)
};

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

/**
 * Resilient content generation with automatic model fallback and exponential backoff.
 * Handles 404 errors by triggering the AI Studio key selection dialog if necessary.
 */
async function safeGenerateContent(
  primaryModel: string, 
  prompt: string, 
  instruction: string, 
  jsonMode: boolean = false, 
  schema?: any
) {
  // Define the chain of fallback models based on the primary request
  const modelChain = [primaryModel];
  if (primaryModel === MODELS.PREMIUM) modelChain.push(MODELS.STANDARD, MODELS.FALLBACK);
  else if (primaryModel === MODELS.STANDARD) modelChain.push(MODELS.FALLBACK);

  let lastError = null;

  for (const modelName of modelChain) {
    let retries = 0;
    const maxRetries = 4;

    while (retries <= maxRetries) {
      try {
        // IMPORTANT: Create a new instance right before the call to use the latest API Key
        const apiKey = getEnv("VITE_API_KEY") || getEnv("EXPO_PUBLIC_API_KEY") || getEnv("EXPO_PUBLIC_GEMINI_API_KEY");
        if (!apiKey) {
          throw new Error("MISSING_API_KEY");
        }
        const ai = new GoogleGenAI({ apiKey });
        
        const config: any = { 
          systemInstruction: instruction,
          thinkingConfig: modelName === MODELS.PREMIUM ? { thinkingBudget: 2048 } : { thinkingBudget: 0 }
        };
        
        if (jsonMode) {
          config.responseMimeType = "application/json";
          if (schema) config.responseSchema = schema;
        }

        const response = await ai.models.generateContent({
          model: modelName,
          contents: prompt,
          config: config
        });

        if (!response.text) throw new Error("EMPTY_RESPONSE");
        return response.text;

      } catch (error: any) {
        lastError = error;
        const errorMsg = error.message || "";
        
        // Handle 404 Requested entity was not found - Mandatory for Gemini 3/Veo key management
        if (errorMsg.includes("Requested entity was not found") || errorMsg.includes("404")) {
          console.warn("Resilience: Model not found or Key permissions missing. Triggering key selection.");
          if (typeof window !== 'undefined' && (window as any).aistudio?.openSelectKey) {
            await (window as any).aistudio.openSelectKey();
            // After triggering selection, we retry once with the new assumed key
            retries++;
            continue;
          }
        }

        const isQuotaError = errorMsg.includes("429") || errorMsg.includes("RESOURCE_EXHAUSTED");
        const isInternalError = errorMsg.includes("500") || errorMsg.includes("INTERNAL");
        const isUnavailable = errorMsg.includes("503") || errorMsg.includes("UNAVAILABLE");

        if (isQuotaError || isInternalError || isUnavailable) {
          if (retries < maxRetries) {
            retries++;
            const waitTime = Math.pow(2, retries) * 1200 + Math.random() * 1200;
            console.warn(`Resilience: Model ${modelName} rate limited. Retry ${retries}/${maxRetries} in ${Math.round(waitTime)}ms`);
            await delay(waitTime);
            continue;
          } else {
            console.warn(`Resilience: Model ${modelName} exhausted. Falling back to next tier.`);
            break; 
          }
        }
        throw error;
      }
    }
  }

  throw new Error(`AI_ENGINE_EXHAUSTED: ${lastError?.message || "All models in chain failed."}`);
}

export const extractJobAdvert = async (text: string): Promise<StructuredRequirement> => {
  const schema = {
    type: Type.OBJECT,
    properties: {
      roleTitle: { type: Type.STRING },
      trustName: { type: Type.STRING },
      grade: { type: Type.STRING },
      responsibilities: { type: Type.ARRAY, items: { type: Type.STRING } },
      essentialCriteria: { type: Type.ARRAY, items: { type: Type.STRING } },
      desirableCriteria: { type: Type.ARRAY, items: { type: Type.STRING } },
      competencies: { type: Type.ARRAY, items: { type: Type.STRING } },
      keywords: { type: Type.ARRAY, items: { type: Type.STRING } }
    },
    required: ["roleTitle", "trustName"]
  };
  const textOutput = await safeGenerateContent(MODELS.STANDARD, `Analyze Job Advert: ${text}`, TEXTBOOK_SYSTEM_INSTRUCTION, true, schema);
  return JSON.parse(textOutput || '{}');
};

export const generateQuestions = async (grade: NHSGrade, specialty: string, type: InterviewType, reqs: StructuredRequirement, docs: KnowledgeDoc[] = [], count: number = 20, avoidQuestions: string[] = []): Promise<Question[]> => {
  const querySeed = [
    grade,
    specialty,
    type,
    reqs?.roleTitle,
    reqs?.trustName,
    ...(reqs?.keywords || []),
    ...(reqs?.competencies || [])
  ].filter(Boolean).join(" ");
  const userContext = buildKnowledgeContext(docs, { query: querySeed, maxChars: 12000 });
  const avoidList = Array.from(new Set(avoidQuestions)).slice(0, 40);

  const bookCategories = [
    "Portfolio/Motivation",
    "Skills/Leadership/Teamwork",
    "Clinical Governance/EBM/Audit",
    "Ethics/Consent/Confidentiality",
    "Difficult Scenarios/Prioritisation",
    "Communication/Role-play",
    "Presentation/Group Discussion"
  ];

  const fixedDistributions: Record<number, Record<string, number>> = {
    10: {
      "Portfolio/Motivation": 2,
      "Skills/Leadership/Teamwork": 2,
      "Clinical Governance/EBM/Audit": 2,
      "Ethics/Consent/Confidentiality": 1,
      "Difficult Scenarios/Prioritisation": 2,
      "Communication/Role-play": 1,
      "Presentation/Group Discussion": 0
    },
    20: {
      "Portfolio/Motivation": 4,
      "Skills/Leadership/Teamwork": 4,
      "Clinical Governance/EBM/Audit": 3,
      "Ethics/Consent/Confidentiality": 3,
      "Difficult Scenarios/Prioritisation": 3,
      "Communication/Role-play": 2,
      "Presentation/Group Discussion": 1
    }
  };

  let distributionInstruction = "";
  if (type === InterviewType.Mixed) {
    const dist = fixedDistributions[count] || fixedDistributions[20];
    const formatted = Object.entries(dist).map(([k, v]) => `${k}: ${v}`).join(", ");
    distributionInstruction = `Use the Medical Interviews (ISC, 4th Ed) station balance. Create EXACT counts per category: ${formatted}. Use only these categories.`;
  } else {
    distributionInstruction = `Focus 100% of questions on the ${type} domain.`;
  }

  const prompt = `Generate exactly ${count} high-yield NHS interview stations for ${grade} in ${specialty}.
${userContext ? `Context (core reference material): ${userContext}.` : ''}
You must output ONLY English.
If Mixed: ${distributionInstruction}
If not Mixed: ${distributionInstruction}
Output each station with a category tag (domainTags) from this list only: ${bookCategories.join(", ")}.
Order the output by category groups in this exact sequence: ${bookCategories.join(" | ")}.
Avoid asking any of these previous questions verbatim or as close paraphrases: ${avoidList.length ? avoidList.join(" | ") : "None"}.
Question style rules:
- Keep the question stem concise and interview-style, aligned with ISC Medical Interviews (4th Ed).
- Target 20-45 words per question. If a scenario is required, max 2 sentences and 60 words total.
- Avoid multi-part questions unless clinically necessary (no more than 2 parts).
Real-interview sequencing rules (from the book):
- Start with 1 warm-up station in Portfolio/Motivation or Background/Motivation style.
- Follow with 1 Skills/Leadership/Teamwork station.
- Middle block should alternate between Clinical Governance/EBM/Audit, Ethics/Consent/Confidentiality, and Difficult Scenarios/Prioritisation.
- Include 1 Communication/Role-play station after the mid-block.
- If count allows, end with Presentation/Group Discussion as a final station (only if that category is included by the distribution).
- Progress difficulty from easier to harder within each category group.
Do not include any extra text outside the JSON.`;

  const schema = {
    type: Type.ARRAY,
    items: {
      type: Type.OBJECT,
      properties: {
        text: { type: Type.STRING },
        domainTags: { type: Type.ARRAY, items: { type: Type.STRING } },
        difficulty: { type: Type.STRING },
        interviewerFocus: { type: Type.STRING },
        pitfalls: { type: Type.ARRAY, items: { type: Type.STRING } },
        modelAnswer: { type: Type.OBJECT, properties: { points: { type: Type.ARRAY, items: { type: Type.STRING } }, structure: { type: Type.STRING } } },
        rubric: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { category: { type: Type.STRING }, description: { type: Type.STRING }, maxScore: { type: Type.NUMBER } } } }
      },
      required: ["text", "domainTags", "difficulty", "modelAnswer"]
    }
  };
  
  const textOutput = await safeGenerateContent(MODELS.STANDARD, prompt, TEXTBOOK_SYSTEM_INSTRUCTION, true, schema);
  const parsed = JSON.parse(textOutput || '[]');
  const normalized = parsed.slice(0, count).map((q: any) => {
    const tags = Array.isArray(q.domainTags) ? q.domainTags : [];
    const filteredTags = tags.filter((t: string) => bookCategories.includes(t));
    return {
      ...q,
      domainTags: filteredTags.length ? filteredTags : [bookCategories[0]],
      id: Math.random().toString(36).substr(2, 9),
      isPinned: false
    };
  });
  return normalized;
};

export const evaluateAnswer = async (question: Question, answer: string, grade: NHSGrade, specialty: string, thread: { q: string, a: string }[], context?: string): Promise<Feedback> => {
  const schema = {
    type: Type.OBJECT,
    properties: {
      scores: { 
        type: Type.OBJECT, 
        properties: { 
          Communication: { type: Type.INTEGER }, 
          'Patient Safety': { type: Type.INTEGER }, 
          'NHS Values': { type: Type.INTEGER }, 
          'Clinical Reasoning': { type: Type.INTEGER }, 
          Leadership: { type: Type.INTEGER }, 
          Prioritisation: { type: Type.INTEGER }, 
          Professionalism: { type: Type.INTEGER } 
        } 
      },
      overallScore: { type: Type.INTEGER },
      pros: { type: Type.ARRAY, items: { type: Type.STRING } },
      cons: { type: Type.ARRAY, items: { type: Type.STRING } },
      blindSpots: { type: Type.ARRAY, items: { type: Type.STRING } },
      suggestedAnswer: { type: Type.STRING },
      hurdleFeedback: { type: Type.STRING },
      hurdlePros: { type: Type.ARRAY, items: { type: Type.STRING } },
      hurdleCons: { type: Type.ARRAY, items: { type: Type.STRING } },
      hurdleIdealAnswer: { type: Type.STRING },
      drills: { type: Type.ARRAY, items: { type: Type.STRING } },
      frameworkUsed: { type: Type.STRING }
    },
    required: ["overallScore", "suggestedAnswer", "scores", "blindSpots"]
  };
  
  const threadContext = thread.map(t => `Q: ${t.q}\nA: ${t.a}`).join("\n\n");
  const prompt = `Evaluate interaction using Medical Interviews (ISC, 4th Ed) principles. 
Grade: ${grade}. Specialty: ${specialty}. Station: ${question.text}.
Transcript: ${threadContext}.
${context ? `Context: ${context}.` : ''}
Assess structure (clear framework), evidence (specific examples), prioritisation/safety, communication, and NHS values.
Provide concise, actionable feedback and keep everything in English.`;

  const textOutput = await safeGenerateContent(MODELS.PREMIUM, prompt, TEXTBOOK_SYSTEM_INSTRUCTION, true, schema);
  return JSON.parse(textOutput || '{}');
};

export const generateFollowUp = async (question: string, userAnswer: string, history: { q: string, a: string }[]): Promise<string> => {
  return await safeGenerateContent(MODELS.STANDARD, `Original Question: ${question}\nUser Answer: "${userAnswer}". Generate one professional follow-up 'hurdle' question.`, "Output only the follow-up question.") || "How would you ensure patient safety in this specific scenario?";
};

export const generateStrategicPlan = async (sessions: Session[]): Promise<{ title: string; advice: string }[]> => {
  if (sessions.length === 0) return [];
  const schema = { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { title: { type: Type.STRING }, advice: { type: Type.STRING } }, required: ["title", "advice"] } };
  const textOutput = await safeGenerateContent(MODELS.PREMIUM, `Analyze trends in sessions: ${JSON.stringify(sessions)}. Provide 5 high-impact strategic advice points.`, TEXTBOOK_SYSTEM_INSTRUCTION, true, schema);
  return JSON.parse(textOutput || '[]');
};

export const generateBlueprintAnswers = async (journey: Journey, knowledgeContext?: string): Promise<Record<string, string>> => {
  const schema = {
    type: Type.ARRAY,
    items: {
      type: Type.OBJECT,
      properties: {
        questionId: { type: Type.STRING },
        answer: { type: Type.STRING }
      },
      required: ["questionId", "answer"]
    }
  };

  const requirements = journey.requirements ? JSON.stringify(journey.requirements) : "None";
  const candidateProfile = journey.candidateProfile || "None";
  const extraDocs = journey.additionalDocs?.map(d => d.content || '').filter(Boolean).join("\n\n") || "None";

  const prompt = `Generate best-in-class NHS interview answers for every station below.\n\n` +
    `Grade: ${journey.grade}\nSpecialty: ${journey.specialty}\nInterview Type: ${journey.interviewType}\n` +
    `Job Advert Requirements: ${requirements}\nCandidate Profile: ${candidateProfile}\nAdditional Documents: ${extraDocs}\n\n` +
    `${knowledgeContext ? `Core Reference Material:\n${knowledgeContext}\n\n` : ''}` +
    `Rules:\n- Output ONLY English.\n- Use the ISC Medical Interviews (4th Ed) frameworks and NHS values.\n- Each answer must be concise, structured, and safety-aware.\n- Avoid filler and do not mention that you are an AI.\n- Return JSON only with questionId + answer.\n\n` +
    `Questions:\n${JSON.stringify(journey.questions.map(q => ({ id: q.id, text: q.text, domainTags: q.domainTags, difficulty: q.difficulty })))}\n`;

  const textOutput = await safeGenerateContent(MODELS.PREMIUM, prompt, TEXTBOOK_SYSTEM_INSTRUCTION, true, schema);
  const parsed = JSON.parse(textOutput || '[]');
  const result: Record<string, string> = {};
  parsed.forEach((item: any) => {
    if (item?.questionId && typeof item.answer === 'string') {
      result[item.questionId] = item.answer.trim();
    }
  });
  return result;
};
